/**
 * Sync Queue — Uploads pending offline items to the backend.
 *
 * Processing order per AC #5:
 *   1. site visit creates (so real IDs exist)
 *   2. checklist item creates (use real visit ID)
 *   3. site visit updates
 *   4. checklist item updates
 *   Photos are uploaded immediately after the visit they belong to.
 *
 * Called by useOfflineSync hook (not the service worker directly).
 * The service worker notifies the app, the app provides a fresh Firebase token.
 */
import { offlineQueue, type OfflineQueueEntry } from './offlineQueue';
import { offlinePhotos } from './offlinePhotos';

export interface SyncProgress {
  total: number;
  completed: number;
  currentItem: string | null;
  errors: Array<{ id: string; action: string; error: string }>;
}

export type SyncProgressCallback = (progress: SyncProgress) => void;

const API_BASE = import.meta.env.VITE_API_URL || '';

async function apiFetch(
  path: string,
  method: string,
  token: string,
  body?: unknown
): Promise<Response> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res;
}

async function uploadPhoto(
  visitId: string,
  token: string,
  photo: Awaited<ReturnType<typeof offlinePhotos.getByQueueEntry>>[number]
): Promise<void> {
  const formData = new FormData();
  formData.append('file', photo.blob, photo.filename);
  if (photo.caption) {
    formData.append('caption', photo.caption);
  }

  const res = await fetch(`${API_BASE}/api/site-visits/${visitId}/photos`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Photo upload HTTP ${res.status}: ${text}`);
  }
}

function sortByPriority(entries: OfflineQueueEntry[]): OfflineQueueEntry[] {
  const order: Record<string, number> = {
    create_visit: 0,
    create_checklist_item: 1,
    update_visit: 2,
    update_checklist_item: 3,
  };
  return [...entries].sort((a, b) => {
    const pa = order[a.action] ?? 9;
    const pb = order[b.action] ?? 9;
    if (pa !== pb) return pa - pb;
    return a.offline_timestamp - b.offline_timestamp;
  });
}

export async function syncPendingItems(
  getToken: () => Promise<string>,
  onProgress?: SyncProgressCallback
): Promise<SyncProgress> {
  const pending = await offlineQueue.getPending();
  const sorted = sortByPriority(pending);

  const progress: SyncProgress = {
    total: sorted.length,
    completed: 0,
    currentItem: null,
    errors: [],
  };

  if (sorted.length === 0) return progress;

  const token = await getToken();

  for (let i = 0; i < sorted.length; i++) {
    const entry = sorted[i];
    await offlineQueue.setStatus(entry.id, 'syncing');
    progress.currentItem = entry.action.replace(/_/g, ' ');
    onProgress?.({ ...progress });

    try {
      if (entry.action === 'create_visit') {
        await syncCreateVisit(entry, token);
        // updateChildVisitId() updates IndexedDB, but the in-memory sorted[] still
        // holds stale local visit_ids. Re-read remaining entries so child checklist
        // items use the real server visit_id when they are processed next.
        const fresh = await offlineQueue.getPending();
        for (let j = i + 1; j < sorted.length; j++) {
          const refreshed = fresh.find((e) => e.id === sorted[j].id);
          if (refreshed) sorted[j] = refreshed;
        }
      } else if (entry.action === 'update_visit') {
        await syncUpdateVisit(entry, token);
      } else if (entry.action === 'create_checklist_item') {
        await syncCreateChecklistItem(entry, token);
      } else if (entry.action === 'update_checklist_item') {
        await syncUpdateChecklistItem(entry, token);
      }

      await offlineQueue.setStatus(entry.id, 'done');
      progress.completed++;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      await offlineQueue.recordFailure(entry.id, errorMsg, entry.retry_count + 1);
      progress.errors.push({ id: entry.id, action: entry.action, error: errorMsg });
    }

    onProgress?.({ ...progress });
  }

  // Clean up done entries
  await offlineQueue.clearDone();
  progress.currentItem = null;
  onProgress?.({ ...progress });

  return progress;
}

async function syncCreateVisit(entry: OfflineQueueEntry, token: string): Promise<void> {
  const res = await apiFetch(
    `/api/projects/${entry.project_id}/site-visits`,
    'POST',
    token,
    {
      ...entry.data,
      offline_timestamp: entry.offline_timestamp,
    }
  );
  const json = await res.json();
  const realVisitId: string = json.site_visit?.id;

  if (!realVisitId) throw new Error('Backend did not return visit id');

  // Update child queue entries that reference the local offline visit ID
  await offlineQueue.updateChildVisitId(entry.entity_id, realVisitId);

  // Upload photos for this visit
  const photos = await offlinePhotos.getByQueueEntry(entry.id);
  for (const photo of photos) {
    await uploadPhoto(realVisitId, token, photo);
    await offlinePhotos.delete(photo.id);
  }
}

async function syncUpdateVisit(entry: OfflineQueueEntry, token: string): Promise<void> {
  await apiFetch(
    `/api/site-visits/${entry.entity_id}`,
    'PATCH',
    token,
    {
      ...entry.data,
      offline_timestamp: entry.offline_timestamp,
    }
  );

  // Upload any photos queued for this update
  const photos = await offlinePhotos.getByQueueEntry(entry.id);
  for (const photo of photos) {
    await uploadPhoto(entry.entity_id, token, photo);
    await offlinePhotos.delete(photo.id);
  }
}

async function syncCreateChecklistItem(entry: OfflineQueueEntry, token: string): Promise<void> {
  const visitId = entry.visit_id;
  if (!visitId) throw new Error('Checklist item missing visit_id');

  await apiFetch(
    `/api/site-visits/${visitId}/checklist-items`,
    'POST',
    token,
    entry.data
  );
}

async function syncUpdateChecklistItem(entry: OfflineQueueEntry, token: string): Promise<void> {
  const visitId = entry.visit_id;
  if (!visitId) throw new Error('Checklist item update missing visit_id');

  await apiFetch(
    `/api/site-visits/${visitId}/checklist-items/${entry.entity_id}`,
    'PATCH',
    token,
    entry.data
  );
}

/** Register a Background Sync tag (Chrome/Edge only). Noop on unsupported browsers. */
export async function registerBackgroundSync(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const syncManager = (reg as any).sync;
    if (syncManager) {
      await syncManager.register('pwa-offline-sync');
    }
  } catch {
    // Not supported — polling fallback handles it
  }
}
